import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomerBalanceApi from "../../../infrastructure/api/customerBalance";
import { formatRial, pick } from "./paymentGatewayUtils";

const normalizeBalance = (res) => {
  if (res?.isSuccess === false) {
    return { error: res?.message || "بارگذاری اعتبار ناموفق بود.", data: null };
  }
  const data = res?.data ?? res?.Data ?? res;
  return { error: null, data };
};

const PanelBalanceCard = ({
  compact = false,
  showHistoryLink = true,
  onBalanceLoaded,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(null);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CustomerBalanceApi.getBalance();
      const { error: err, data } = normalizeBalance(res);
      if (err) {
        setError(err);
        setBalance(null);
      } else {
        setBalance(data);
        onBalanceLoaded?.(data);
      }
    } catch {
      setError("بارگذاری اعتبار پنل ناموفق بود.");
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, [onBalanceLoaded]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const isEnabled = pick(balance, "isEnabled", "IsEnabled") !== false;
  const available = Number(
    pick(balance, "availableBalance", "AvailableBalance") ?? 0
  );

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="35%" />
        </Stack>
      </Paper>
    );
  }

  if (!isEnabled) return null;

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
        <Button size="small" onClick={loadBalance}>
          تلاش مجدد
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceWalletOutlinedIcon color="primary" fontSize="small" />
          <Typography variant={compact ? "subtitle2" : "subtitle1"} fontWeight={600}>
            مانده اعتبار پنل
          </Typography>
        </Stack>

        <Typography variant={compact ? "h6" : "h5"} color="primary.main" fontWeight={700}>
          {formatRial(available)}
        </Typography>

        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/SLN/Customer/Balance/TopUp")}
          >
            افزایش اعتبار
          </Button>
          {showHistoryLink ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate("/SLN/Customer/Balance/History")}
            >
              تاریخچه اعتبار
            </Button>
          ) : null}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          اعتبار پنل فقط برای پرداخت فاکتورهای سولارنت قابل استفاده است.
        </Typography>
      </Stack>
    </Paper>
  );
};

PanelBalanceCard.propTypes = {
  compact: PropTypes.bool,
  showHistoryLink: PropTypes.bool,
  onBalanceLoaded: PropTypes.func,
};

export default PanelBalanceCard;
